import { z } from 'zod';
import { APARATOS_SISTEMA, SCHEMA_VERSION, type AparatoSistema } from './tipos.js';

/**
 * Contrato app → backend (Anexo B de la especificación, refinado).
 *
 * Cambios respecto del borrador v1.2 del anexo (ver docs/04-contrato-api.md):
 *  - schemaVersion "1.3".
 *  - consentimientos.datosPersonales: aceptación expresa Ley 25.326 (la
 *    pantalla HU-03 la pide; el borrador la daba por implícita).
 *  - evaluacion/consentimientos/firmaTrabajador son opcionales a nivel de
 *    esquema y obligatorios por regla de negocio según `resultado` y
 *    contingencia (superRefine más abajo).
 *  - envio.whatsapp se acepta pero se ignora en v1 (decisión de producto 6).
 */

export const esquemaGeo = z.object({
  lat: z.number().min(-90).max(90),
  lng: z.number().min(-180).max(180),
  precisionMetros: z.number().nonnegative(),
  proveedor: z.string().min(1).max(40),
  esSimulada: z.boolean(),
  capturadaEn: z.string().datetime({ offset: true }),
});

export const esquemaEvaluacion = z.object({
  // Dictamen de aptitud laboral: el entregable del médico de control. NO
  // diagnostica (eso es del médico tratante); sólo verifica y dictamina si el
  // trabajador está apto o no para trabajar (ver ADR-0009).
  aptitud: z.enum(['APTO_PARA_TRABAJAR', 'NO_APTO_TEMPORAL']),
  // Fecha de retorno laboral: la carga el médico directamente (no días de
  // reposo a convertir). Null cuando está apto; obligatoria si no lo está —
  // regla de negocio en el servidor (ver controles.ts).
  fechaReinsercion: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable(),
  // Aparato o sistema afectado: clasificación gruesa (no diagnóstico CIE-10),
  // por eso es dato laboral y no queda detrás del secreto profesional — lo ve
  // también RRHH. Null cuando está apto.
  aparatoSistema: z.enum(APARATOS_SISTEMA as [AparatoSistema, ...AparatoSistema[]]).nullable(),
  // Nota reservada del profesional (sólo Servicio Médico la ve, no RRHH).
  observaciones: z.string().max(2000).default(''),
});

export const esquemaConsentimientos = z.object({
  devolucionRecibida: z.boolean(),
  datosPersonales: z.boolean(),
  textoVersion: z.string().min(1).max(40),
  envio: z.object({
    email: z.boolean(),
    whatsapp: z.boolean().optional().default(false),
  }),
});

export const esquemaFirmaTrabajador = z.object({
  tipo: z.literal('GRAFOMETRICA'),
  imagenPng: z.string().min(100), // base64 del PNG del canvas
  trazos: z.object({
    formato: z.string().min(1).max(60),
    datos: z.string().min(2), // JSON de puntos, comprimible, en base64 o texto
  }),
  capturadaEn: z.string().datetime({ offset: true }),
  duracionMs: z.number().int().nonnegative(),
  cantidadTrazos: z.number().int().positive(),
  hashSha256: z.string().regex(/^[a-f0-9]{64}$/),
});

export const esquemaAdjuntoRef = z.object({
  tipo: z.enum(['CONSTANCIA_PAPEL_FOTO', 'FACHADA_FOTO', 'EVIDENCIA_FOTO']),
  uploadId: z.string().min(1),
  hashSha256: z.string().regex(/^[a-f0-9]{64}$/),
  capturadaEn: z.string().datetime({ offset: true }),
  paginas: z.number().int().positive().default(1),
});

export const esquemaEvidenciaConectividad = z.object({
  estadoRed: z.enum(['SIN_SERVICIO', 'SIN_DATOS', 'TIMEOUT_BACKEND', 'SIMULADA_DEMO']),
  intentosFallidos: z
    .array(
      z.object({
        en: z.string().datetime({ offset: true }),
        error: z.string().max(200),
      }),
    )
    .max(100),
});

export const esquemaPayloadControl = z
  .object({
    schemaVersion: z.literal(SCHEMA_VERSION),
    idempotencyKey: z.string().uuid(),
    orden: z.object({
      ordenId: z.string().min(1),
      empresaClienteId: z.string().min(1),
      tipoControl: z.string().min(1),
    }),
    resultado: z.enum([
      'REALIZADA',
      'PACIENTE_AUSENTE',
      'RECHAZA_CONTROL',
      'DOMICILIO_ERRONEO',
      'MEDICO_IMPOSIBILITADO',
    ]),
    motivoNoRealizada: z.string().max(2000).optional(),
    profesional: z.object({
      profesionalId: z.string().min(1),
      matricula: z.object({
        tipo: z.enum(['MN', 'MP']),
        numero: z.string().min(1).max(20),
        jurisdiccion: z.string().min(2).max(10),
      }),
    }),
    trabajador: z.object({
      trabajadorId: z.string().min(1),
      dni: z.string().regex(/^\d{7,9}$/),
    }),
    evaluacion: esquemaEvaluacion.optional(),
    consentimientos: esquemaConsentimientos.optional(),
    firmaTrabajador: esquemaFirmaTrabajador.optional(),
    firmaProfesional: z.object({
      modo: z.literal('REMOTA_POR_OPERACION'),
      proveedorId: z.string().min(1),
    }),
    geo: esquemaGeo,
    dispositivo: z.object({
      plataforma: z.string().min(1).max(30),
      osVersion: z.string().max(60),
      appVersion: z.string().max(30),
      deviceIdHash: z.string().max(80),
      integridad: z.object({
        mecanismo: z.enum(['playIntegrity', 'appAttest', 'webDemo']),
        veredicto: z.string().max(60),
        evaluadoEn: z.string().datetime({ offset: true }),
      }),
    }),
    contingencia: z.object({
      activa: z.boolean(),
      evidenciaConectividad: esquemaEvidenciaConectividad.nullable(),
      adjuntos: z.array(esquemaAdjuntoRef).max(20).default([]),
    }),
    rectificaA: z
      .string()
      .regex(/^ACM-\d{4}-\d{6}-\d{2}$/)
      .optional(),
  })
  .superRefine((p, ctx) => {
    const esPapel =
      p.contingencia.activa &&
      p.contingencia.adjuntos.some((a) => a.tipo === 'CONSTANCIA_PAPEL_FOTO');

    if (p.resultado === 'REALIZADA') {
      if (!esPapel) {
        if (!p.evaluacion)
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['evaluacion'],
            message: 'La evaluación es obligatoria en un control realizado digitalmente',
          });
        if (!p.firmaTrabajador)
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['firmaTrabajador'],
            message:
              'La firma grafométrica del trabajador es obligatoria (o derivar a rechazo, HU-12)',
          });
        if (!p.consentimientos)
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['consentimientos'],
            message: 'Los consentimientos del trabajador son obligatorios',
          });
      }
      if (p.consentimientos) {
        if (!p.consentimientos.devolucionRecibida || !p.consentimientos.datosPersonales)
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['consentimientos'],
            message:
              'La recepción de la devolución y la aceptación de la Ley 25.326 son obligatorias para continuar',
          });
      }
    } else {
      if (!p.motivoNoRealizada || p.motivoNoRealizada.trim().length < 5)
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['motivoNoRealizada'],
          message: 'Debe registrarse el motivo/observaciones del control no realizado',
        });
    }

    if (p.contingencia.activa && !p.contingencia.evidenciaConectividad)
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['contingencia', 'evidenciaConectividad'],
        message:
          'La contingencia debe acreditarse con la evidencia automática de conectividad (Dec. 407/2026)',
      });
  });

export type PayloadControl = z.infer<typeof esquemaPayloadControl>;
export type EvaluacionControl = z.infer<typeof esquemaEvaluacion>;
export type ConsentimientosControl = z.infer<typeof esquemaConsentimientos>;
export type FirmaTrabajadorPayload = z.infer<typeof esquemaFirmaTrabajador>;
